import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"
import { CircleAlert, CircleCheck, Info, LucideProps } from "lucide-react"

const borderBoxVariants = cva(
  "border rounded-md inline-flex items-center transition-colors w-full",
  {
    variants: {
      variant: {
        default: "bg-lighter-gray",
        info: "text-blue-500 bg-blue-100 border-blue-200",
        danger: "text-red-500 bg-red-100 border-red-200",
        success: "text-green-500 bg-green-100 border-green-200",
        accent: "text-orange-500 bg-orange-100 border-orange-200",
      },
      size: {
        default: "p-3 gap-3",
        sm: "p-2 text-sm gap-2",
        xs: "p-1 px-2 text-xs gap-1",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface BorderBoxProps
  extends React.HTMLAttributes<HTMLDivElement>,
  VariantProps<typeof borderBoxVariants> {
  asChild?: boolean
  icon?: React.ReactElement | boolean
  iconProps?: LucideProps & React.RefAttributes<SVGSVGElement>
}

const BorderBox = React.forwardRef<HTMLDivElement, BorderBoxProps>(
  ({ className, variant, size, asChild = false, children, icon, iconProps = { size: 16 }, ...props }, ref) => {
    const Comp = asChild ? Slot : "div"

    // Fix the Icon declaration and logic
    let Icon: React.ReactElement | undefined = undefined

    if (icon !== false) {
      if (React.isValidElement(icon)) {
        Icon = icon
      } else {
        switch (variant) {
          case "info":
            Icon = <Info {...iconProps} className={cn(iconProps.className, "text-blue-500")} />
            break
          case "danger":
            Icon = <CircleAlert {...iconProps} className={cn(iconProps.className, "text-red-500")} />
            break
          case "success":
            Icon = <CircleCheck {...iconProps} className={cn(iconProps.className, "text-green-500")} />
            break
          default:
            Icon = undefined
            break
        }
      }
    }

    return (
      <Comp
        className={cn(borderBoxVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        {Icon && (
          <span className="flex-shrink-0">
            {Icon}
          </span>
        )}
        {children}
      </Comp>
    )
  }
)
BorderBox.displayName = "BorderBox"

export { BorderBox, borderBoxVariants }
